import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Search, UserCheck, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { type Player, PlayerTable } from '@/components/players/PlayerTable';
import { Skeleton } from '@/components/ui/skeleton';
import { useListUsersQuery, useListRolesQuery, type RoleDto } from '@/store/apiSlice';

export function PlayerContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') ?? '');
  const selectedTeam = searchParams.get('team') ?? '';
  const statusParam = searchParams.get('status');

  const [players, setPlayers] = useState<Player[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [prevPage, setPrevPage] = useState<string | null>(null);
  const [teams, setTeams] = useState<{ id: number; name: string; code: string }[]>([]);

  const statusFilter =
    statusParam === 'true' ? 'active' : statusParam === 'false' ? 'inactive' : 'all';

  const createQueryString = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams.toString());
      Object.entries(updates).forEach(([key, value]) => {
        if (!value || value === 'all') params.delete(key);
        else params.set(key, value);
      });
      return params.toString();
    },
    [searchParams]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      navigate(`${location.pathname}?${createQueryString({ q: searchQuery || undefined })}`, {
        replace: true,
      });
    }, 500);
    return () => clearTimeout(t);
  }, [searchQuery, location.pathname, navigate, createQueryString]);

  const handleStatusChange = (value: string) => {
    const status =
      value === 'active' ? 'true' : value === 'inactive' ? 'false' : undefined;
    navigate(
      `${location.pathname}?${createQueryString({ status, team: undefined, page: undefined })}`,
      { replace: true }
    );
  };

  const handleTeamClick = (team: string) => {
    navigate(
      `${location.pathname}?${createQueryString({ team: team || undefined, page: '1' })}`,
      { replace: true }
    );
  };

  const handleSort = (field: string, direction: string) => {
    const apiField =
      field === 'last_active'
        ? 'last_login'
        : field === 'joined'
          ? 'date_joined'
          : field === 'name'
            ? 'full_name'
            : field;
    navigate(
      `${location.pathname}?${createQueryString({ sort: `${apiField}_${direction}`, page: '1' })}`,
      { replace: true }
    );
  };

  const handlePageClick = (url: string | null) => {
    if (!url) return;
    try {
      const parsed = new URL(url, window.location.origin);
      const page = parsed.searchParams.get('page');
      if (page)
        navigate(`${location.pathname}?${createQueryString({ page })}`, { replace: true });
    } catch {}
  };

  const team = searchParams.get('team') ?? undefined;
  const status = searchParams.get('status') ?? undefined;
  const q = searchParams.get('q') ?? undefined;
  const page = searchParams.get('page') ?? undefined;
  const sort = searchParams.get('sort') || 'score_desc';

  const { data: usersData, isLoading } = useListUsersQuery({
    team,
    status,
    q,
    page,
    sort,
  });

  const { data: rolesData } = useListRolesQuery();

  useEffect(() => {
    if (!rolesData) return;
    const rows = (rolesData as RoleDto[]).map((r) => ({
      id: r.id,
      name: r.name ?? '',
      code: r.code ?? '',
    }));
    setTeams(rows);
  }, [rolesData]);

  useEffect(() => {
    if (!usersData) return;
    const rawResults = usersData.results ?? [];
    const count = usersData.count ?? rawResults.length;
    const totalPagesVal = Math.max(1, usersData.total_pages ?? 1);
    const currentPageVal = usersData.current_page ?? 1;
    const pageSize =
      count > 0 && totalPagesVal > 0
        ? Math.ceil(count / totalPagesVal)
        : Math.max(rawResults.length, 1);
    const baseRank = (currentPageVal - 1) * pageSize;

    const mapped: Player[] = rawResults.map((u, index) => ({
      user_id: String(u.id),
      user_full_name: u.full_name?.trim() || u.username || u.email || 'User',
      user_email: u.email,
      user_avatar: u.avatar?.image_url ?? '',
      team: (u.team ?? u.role ?? '').trim(),
      is_active: Boolean(u.is_active),
      is_verified: Boolean(u.is_verified),
      last_active: u.last_login ?? u.updated_at ?? u.date_joined ?? '',
      date_joined: u.date_joined ?? '',
      rank: baseRank + index + 1,
    }));

    setPlayers(mapped);
    setCurrentPage(currentPageVal);
    setNextPage(usersData.next ?? null);
    setPrevPage(usersData.previous ?? null);
    setTotalPages(totalPagesVal);
  }, [usersData]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex-1 flex flex-col sm:flex-row gap-3 items-start sm:items-center w-full">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search players by name, email, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full"
            />
          </div>
          <div className="flex flex-wrap gap-1 border rounded-md p-1 bg-muted/30">
            <Button
              variant={!selectedTeam && statusFilter === 'all' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => handleStatusChange('all')}
            >
              <Users className="h-4 w-4 mr-2" />
              All Players
            </Button>
            <Button
              variant={!selectedTeam && statusFilter === 'active' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => handleStatusChange('active')}
            >
              <UserCheck className="h-4 w-4 mr-2 text-green-500" />
              Active
            </Button>
            <Button
              variant={!selectedTeam && statusFilter === 'inactive' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => handleStatusChange('inactive')}
            >
              <Users className="h-4 w-4 mr-2 text-muted-foreground" />
              Inactive
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground">Filter by Team</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={!selectedTeam ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleTeamClick('')}
            className={cn(
              'h-8 px-3 text-xs font-medium',
              !selectedTeam && 'bg-primary/10 text-primary hover:bg-primary/20'
            )}
          >
            All Teams
          </Button>
          {teams.map((team) => (
            <Button
              key={team.id}
              variant={selectedTeam === team.name ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleTeamClick(team.name)}
              className="h-8 px-3 text-xs font-medium flex items-center gap-2"
            >
              <span className={cn('w-2.5 h-2.5 rounded-full bg-muted-foreground')} />
              {team.name.split(' ')[0] || team.code}
            </Button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-border bg-muted/20">
          <h3 className="text-lg font-semibold text-foreground">Player Leaderboard</h3>
        </div>
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : (
          <PlayerTable
            data={players}
            totalCount={usersData?.count}
            totalPages={totalPages}
            currentPage={currentPage}
            onNext={() => handlePageClick(nextPage)}
            onPrev={() => handlePageClick(prevPage)}
            onFirst={() => {
              const params = new URLSearchParams(searchParams.toString());
              params.set('page', '1');
              navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            }}
            onLast={() => {
              const params = new URLSearchParams(searchParams.toString());
              params.set('page', String(totalPages));
              navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            }}
            onSort={(field, direction) => handleSort(field, direction)}
          />
        )}
      </div>
    </div>
  );
}
